import { VideoDevicesInfo, BizResult, Ok } from "@/models/biz_model"
import { KCSDK } from "@/sdk"
import { VideoInputCache } from "@/utils/storage"
import { ConnectionUseCase } from "./connection"
import { DataReportUsecase } from "./data_report"
import {
  ERR_CAMERA_UNMOUNT,
  ERR_NO_CAMERA_PERMISSION,
  ERR_OPEN_CAMERA,
} from "@/models/constants"
import { useEffect } from "react"

export type OnVideoDeviceInfo = (deviceInfo: VideoDevicesInfo) => void
export let videoDevInfo: VideoDevicesInfo | null
type VideoInputStateListener = (isOpen: boolean) => void
const videoInputStateListeners = new Set<VideoInputStateListener>()
export interface VideoInputOperationEvent {
  isOpen: boolean
  phase: "start" | "success" | "error"
}
const videoInputOperationListeners = new Set<
  (event: VideoInputOperationEvent) => void
>()

function notifyVideoInputState(isOpen: boolean): void {
  videoInputStateListeners.forEach((listener) => listener(isOpen))
}

function addVideoInputStateListener(
  listener: VideoInputStateListener
): () => void {
  videoInputStateListeners.add(listener)
  return () => videoInputStateListeners.delete(listener)
}

function notifyVideoInputOperation(event: VideoInputOperationEvent): void {
  videoInputOperationListeners.forEach((listener) => listener(event))
}

function addVideoInputOperationListener(
  listener: (event: VideoInputOperationEvent) => void
): () => void {
  videoInputOperationListeners.add(listener)
  return () => videoInputOperationListeners.delete(listener)
}

/**
 * 开始获取视频流，在连接上云桌面后调用
 *
 * @param callback 获取到视频设备信息的回调
 */
export function startVideo(callback: OnVideoDeviceInfo): void {
  if (!navigator.mediaDevices?.enumerateDevices) {
    videoDevInfo = {
      outputs: [],
      inputs: [],
      outputVolume: 100,
      inputVolume: 100,
    }
    callback(videoDevInfo)
    return
  }
  void navigator.mediaDevices
    .enumerateDevices()
    .then((devices) => {
      videoDevInfo = {
        outputs: [],
        inputs: devices
          .filter((device) => device.kind === "videoinput")
          .map((device, index) => ({
            index,
            name: device.label || `摄像头 ${index + 1}`,
          })),
        outputVolume: 100,
        inputVolume: 100,
      }
      callback(videoDevInfo)
    })
    .catch((error) => {
      console.error("Start video error: ", error)
      videoDevInfo = {
        outputs: [],
        inputs: [],
        outputVolume: 100,
        inputVolume: 100,
      }
      callback(videoDevInfo)
    })
}

/**
 * 停止获取视频流，在断开云桌面连接后调用
 */
export function stopVideo(): void {
  videoDevInfo = null
}

/**
 * 切换视频输入输出设备
 *
 * @param inputDev 输入设备 index
 * @param outputDev 输出设备 index
 * @returns
 */
export async function selectDevice(
  inputDev: number,
  outputDev: number
): Promise<BizResult<number>> {
  return new Ok(0)
}

/**
 * 设置是否启用视频输出设备
 *
 * @param enable
 * @returns
 */
export function setVideoOutputEnable(enable: boolean): BizResult<number> {
  return new Ok(0)
}

// 返回缓存的视频输入状态
function isVideoInputEnabled(): boolean {
  const deviceId = ConnectionUseCase.getConnectedDevice()?.id
  if (deviceId === undefined) {
    return false
  }
  return VideoInputCache.getVideoInputEnable(deviceId)
}

/**
 * 设置输出设备音量
 *
 * @param volume 音量值，范围 0-100
 */
export function setOutputVolume(volume: number): BizResult<number> {
  return new Ok(0)
}

/**
 * 设置输入设备音量
 *
 * @param volume 音量值，范围 0-100
 */
export function setInputVolumn(): BizResult<number> {
  return new Ok(0)
}

// 视频输入流
let videoInputStream: MediaStream | null = null
// 主动关闭摄像头时置位，用于区分 track.onended 是主动关闭还是设备意外拔出
let videoInputActivelyClosing = false
// 同一次拔出 onended 与 devicechange 都会触发，去重：记录上次上报时间
let cameraUnmountReportedAt = 0
// 用户切换摄像头后记录的偏好设备 id；下次 openVideoInputStream 优先使用该设备
let preferredVideoInputDeviceId: string | null = null

function reportCameraUnmount(): void {
  const now = Date.now()
  // 5 秒内只报一次，避免 onended + devicechange 重复上报
  if (now - cameraUnmountReportedAt < 5000) return
  cameraUnmountReportedAt = now
  DataReportUsecase.reportError(ERR_CAMERA_UNMOUNT, "摄像头连接已断开")
}

const videoConstraints: MediaTrackConstraints = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 25 },
}

async function getPreferredVideoInputDevice(): Promise<MediaDeviceInfo | null> {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return null
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const videoInputDevices = devices.filter(
      (device) => device.kind === "videoinput"
    )
    const defaultDevice = videoInputDevices.find(
      (device) => device.deviceId === "default"
    )

    // 优先使用系统默认摄像头；没有 default 时使用枚举到的第一个摄像头
    return defaultDevice ?? videoInputDevices[0] ?? null
  } catch (e) {
    console.error("Get video input devices error: ", e)
    return null
  }
}

function getVideoInputConstraints(
  inputDevice: MediaDeviceInfo | null
): MediaTrackConstraints {
  if (!inputDevice?.deviceId || inputDevice.deviceId === "default") {
    return videoConstraints
  }
  return { ...videoConstraints, deviceId: { exact: inputDevice.deviceId } }
}

async function getVideoInputMediaStream(
  inputDevice: MediaDeviceInfo | null
): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: getVideoInputConstraints(inputDevice),
    })
  } catch (error) {
    if (inputDevice === null || inputDevice.deviceId === "default") throw error
    console.warn("Open selected camera failed, retry default: ", error)
    return await navigator.mediaDevices.getUserMedia({
      video: videoConstraints,
    })
  }
}

// 打开视频输入流
async function openVideoInputStream(): Promise<boolean> {
  if (videoInputStream !== null) {
    // 重连后 SDK 可能已换了 PeerConnection；本地流仍可复用，但需要绑定新 sender。
    try {
      await Promise.resolve(KCSDK.enableVideoInput(videoInputStream))
      const deviceId = ConnectionUseCase.getConnectedDevice()?.id
      if (deviceId !== undefined) {
        VideoInputCache.saveVideoInputEnable(deviceId, true)
      }
      notifyVideoInputState(true)
      return true
    } catch (error) {
      console.error("Rebind video input stream error: ", error)
      notifyVideoInputState(false)
      return false
    }
  }

  const deviceId = ConnectionUseCase.getConnectedDevice()?.id
  if (deviceId === undefined) {
    return false
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    console.error("Open input video stream error: getUserMedia not supported")
    return false
  }

  try {
    let inputDevice = await getPreferredVideoInputDevice()
    // 用户曾切换过摄像头时，优先使用其偏好的设备
    if (preferredVideoInputDeviceId !== null) {
      const devices = await getVideoInputDevices()
      const preferred =
        devices?.find((d) => d.deviceId === preferredVideoInputDeviceId) ??
        null
      if (preferred !== null) {
        inputDevice = preferred
      }
    }
    const stream = await getVideoInputMediaStream(inputDevice)

    // 调用 SDK 开启视频输入
    videoInputStream = stream
    // 摄像头意外断开（如 USB 摄像头被拔出）时 track 会 ended；
    // 主动关闭由 videoInputActivelyClosing 标志区分，避免误报。
    cameraUnmountReportedAt = 0
    stream.getVideoTracks().forEach((track) => {
      track.onended = () => {
        if (videoInputActivelyClosing) return
        reportCameraUnmount()
      }
    })

    await Promise.resolve(KCSDK.enableVideoInput(stream))
    // 缓存视频输入状态
    VideoInputCache.saveVideoInputEnable(deviceId, true)
  } catch (e) {
    console.error("Open input video stream error: ", e)
    // 区分权限拒绝与其他开启失败，对齐 android ErrCode.noCameraPermission/openCamera
    if (e instanceof DOMException && (e.name === "NotAllowedError" || e.name === "SecurityError")) {
      DataReportUsecase.reportError(ERR_NO_CAMERA_PERMISSION, "无权限使用摄像头")
    } else {
      DataReportUsecase.reportError(ERR_OPEN_CAMERA, "摄像头开启失败")
    }
    // 授权失败
    videoInputStream?.getTracks().forEach((track) => track.stop())
    videoInputStream = null
    VideoInputCache.saveVideoInputEnable(deviceId, false)
    notifyVideoInputState(false)
    return false
  }

  notifyVideoInputState(true)
  return true
}

// 获取视频输入设备列表
async function getVideoInputDevices(): Promise<MediaDeviceInfo[] | null> {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return null
  }

  const devices = await navigator.mediaDevices.enumerateDevices()
  return devices.filter((device) => device.kind === "videoinput")
}

/**
 * 关闭视频输入流
 *
 * @param isMarkDisable 是否标记为禁用状态
 */
async function closeVideoInputStream(isMarkDisable = true): Promise<void> {
  // 设置视频输入状态缓存
  const deviceId = ConnectionUseCase.getConnectedDevice()?.id
  if (isMarkDisable) {
    if (deviceId !== undefined) {
      VideoInputCache.saveVideoInputEnable(deviceId, false)
    }
  }

  if (videoInputStream === null) {
    // 视频流未开启
    notifyVideoInputState(false)
    return
  }

  const tracks = videoInputStream.getTracks()
  videoInputActivelyClosing = true

  try {
    await Promise.resolve(KCSDK.disableVideoInput())
  } finally {
    tracks.forEach((track) => {
      if (track.readyState !== "ended") track.stop()
      track.onended = null
    })
    videoInputStream = null
    notifyVideoInputState(false)
    videoInputActivelyClosing = false
  }
}

function getVideoInputStream(): MediaStream | null {
  return videoInputStream
}

// 获取当前正在使用的摄像头 deviceId，供 UI 回显选中项
function getCurrentVideoInputDeviceId(): string | null {
  const track = videoInputStream?.getVideoTracks()[0]
  return track?.getSettings().deviceId ?? null
}

/**
 * 热切换摄像头设备。
 *
 * 摄像头未开启时仅记录偏好，下次 openVideoInputStream 使用新设备；
 * 摄像头已开启时复用 SDK 已保留的 videoInputSender，通过 replaceTrack
 * 替换轨道，无需 SDP 重协商。
 *
 * @param deviceId 目标摄像头 deviceId
 * @returns 是否切换成功
 */
async function switchCamera(deviceId: string): Promise<boolean> {
  const devices = await getVideoInputDevices()
  if (devices === null) {
    return false
  }
  const target = devices.find((d) => d.deviceId === deviceId)
  if (target === undefined) {
    console.warn("Switch camera: target device not found: ", deviceId)
    return false
  }
  preferredVideoInputDeviceId = deviceId

  // 摄像头未开启：只记录偏好，不实际采集
  if (videoInputStream === null) {
    return true
  }

  videoInputActivelyClosing = true
  try {
    const newStream = await getVideoInputMediaStream(target)
    const oldStream = videoInputStream

    cameraUnmountReportedAt = 0
    newStream.getVideoTracks().forEach((track) => {
      track.onended = () => {
        if (videoInputActivelyClosing) return
        reportCameraUnmount()
      }
    })

    // SDK 层 videoInputSender 仍存在，命中 replaceTrack 路径，无 SDP 协商
    await KCSDK.enableVideoInput(newStream)
    videoInputStream = newStream

    // 释放旧摄像头硬件
    oldStream.getVideoTracks().forEach((track) => {
      track.stop()
      track.onended = null
    })
    return true
  } catch (e) {
    console.error("Switch camera error: ", e)
    DataReportUsecase.reportError(ERR_OPEN_CAMERA, "摄像头切换失败")
    return false
  } finally {
    videoInputActivelyClosing = false
  }
}

// devicechange 路：设备列表变化时校验当前摄像头是否仍在，拔出则上报（与 onended 双路去重）
async function verifyActiveCameraAvailable(): Promise<void> {
  if (videoInputStream === null || videoInputActivelyClosing) return
  if (!navigator.mediaDevices?.enumerateDevices) return
  const tracks = videoInputStream.getVideoTracks()
  if (tracks.length === 0) return
  const activeDeviceId = tracks[0].getSettings().deviceId
  if (!activeDeviceId) return
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const exists = devices.some(
      (d) => d.kind === "videoinput" && d.deviceId === activeDeviceId
    )
    if (!exists) reportCameraUnmount()
  } catch {
    // 枚举失败不报错
  }
}

function hasActiveVideoInputStream(): boolean {
  return (
    videoInputStream
      ?.getVideoTracks()
      .some((track) => track.readyState === "live") ?? false
  )
}

export const VideoUsecase = {
  isVideoInputEnabled,
  openVideoInputStream,
  closeVideoInputStream,
  getVideoInputStream,
  getVideoInputDevices,
  verifyActiveCameraAvailable,
  addVideoInputStateListener,
  addVideoInputOperationListener,
  getCurrentVideoInputDeviceId,
  switchCamera,
}

// 初始化视频输入流
export const useInitVideoInput = () => {
  useEffect(() => {
    const connectionCallback = {
      onDisconnect: () => {
        // 退出云桌面时断开视频输入流
        VideoUsecase.closeVideoInputStream(false)
      },
      // 摄像头流由 SDK 在新 PeerConnection 建立后统一恢复。
      // 不要在 RECONN 通知阶段操作旧 sender，否则可能与 WebRTC.clear()
      // 及新 PeerConnection 的 recoverVideoInput 产生竞态。
    }
    const agentCameraCallback = {
      onCameraStateChange: (isOpen: boolean) => {
        // Agent 重发当前状态时不重复打开/关闭摄像头。
        if (isOpen && hasActiveVideoInputStream()) return
        if (!isOpen && videoInputStream === null) return

        notifyVideoInputOperation({ isOpen, phase: "start" })
        const operation = isOpen
          ? VideoUsecase.openVideoInputStream().then((success) => {
              notifyVideoInputOperation({
                isOpen,
                phase: success ? "success" : "error",
              })
              return success
            })
          : VideoUsecase.closeVideoInputStream().then(() => {
              notifyVideoInputOperation({ isOpen, phase: "success" })
            })
        operation.catch((error) => {
          notifyVideoInputOperation({ isOpen, phase: "error" })
          console.error("Handle agent camera state change error: ", error)
        })
      },
    }
    ConnectionUseCase.addConnCallback(connectionCallback)
    ConnectionUseCase.addAgentCameraStateCallback(agentCameraCallback)

    if (isVideoInputEnabled()) {
      // 默认开启视频输入，尝试打开视频输入流
      void VideoUsecase.openVideoInputStream()
    }

    return () => {
      ConnectionUseCase.removeConnCallback(connectionCallback)
      ConnectionUseCase.removeAgentCameraStateCallback(agentCameraCallback)
    }
  }, [])
}
